import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { stockBadge, stockPct, stockBarColor, Modal, AdjustModal, useForm } from '../components/helpers';

const EMPTY = { sku: '', name: '', customer: '', supplier_id: '', upc: '', on_hand: '', reorder_at: '', unit_price: '', case_qty: '1', bulk_part_id: '', pack_type: 'bulk', weight_per_unit: '' };
const WASTE_FACTOR = 1.05; // 5% for runners and waste

export default function FinishedGoods() {
  const [items, setItems] = useState([]);
  const [resins, setResins] = useState([]);
  const [components, setComponents] = useState([]);
  const [bulkParts, setBulkParts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [form, set, setForm] = useForm(EMPTY);
  const [skuComponents, setSkuComponents] = useState([]);

  async function load() {
    const [{ data: fg }, { data: raw }, { data: comp }, { data: bp }, { data: sup }] = await Promise.all([
      supabase.from('finished_goods').select('*, bulk_parts(name), suppliers(id,name)').order('customer').order('sku'),
      supabase.from('raw_materials').select('id,name').order('name'),
      supabase.from('components').select('id,name,category').order('category').order('name'),
      supabase.from('bulk_parts').select('id,name').order('name'),
      supabase.from('suppliers').select('id,name').order('name'),
    ]);
    setItems(fg || []);
    setResins(raw || []);
    setComponents(comp || []);
    setBulkParts(bp || []);
    setSuppliers(sup || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function openEdit(item) {
    const packType = item.bulk_part_id ? 'cased' : (Number(item.case_qty) > 1 ? 'cased' : 'bulk');
    setForm({
      sku: item.sku, name: item.name, customer: item.customer || '',
      supplier_id: item.supplier_id || '', upc: item.upc || '',
      on_hand: item.on_hand, reorder_at: item.reorder_at,
      unit_price: item.unit_price || '', case_qty: item.case_qty || '1',
      bulk_part_id: item.bulk_part_id || '', pack_type: packType,
      weight_per_unit: item.weight_per_unit || '',
    });
    const { data } = await supabase.from('sku_components').select('*').eq('finished_good_id', item.id);
    setSkuComponents((data || []).map(c => ({
      type: c.component_type, id: c.resin_id || c.component_id || '',
      qty_per_unit: String(c.qty_per_unit), per_case: c.per_case || false,
    })));
    setEditing(item); setModal('form');
  }

  function openAdd() { setForm(EMPTY); setSkuComponents([]); setEditing(null); setModal('form'); }

  function addSkuComponent(type) {
    setSkuComponents(prev => [...prev, { type, id: '', qty_per_unit: '', per_case: false }]);
  }
  function updateSkuComponent(idx, field, value) {
    setSkuComponents(prev => prev.map((c, i) => {
      if (i !== idx) return c;
      const updated = { ...c, [field]: value };
      // Auto-fill lbs/unit when resin is selected and weight is set
      if (field === 'id' && c.type === 'resin' && form.weight_per_unit) {
        updated.qty_per_unit = (Number(form.weight_per_unit) * WASTE_FACTOR).toFixed(4);
      }
      return updated;
    }));
  }
  function removeSkuComponent(idx) {
    setSkuComponents(prev => prev.filter((_, i) => i !== idx));
  }

  // When weight changes, update any existing resin component's qty_per_unit
  function onWeightChange(e) {
    const weight = e.target.value;
    setForm(f => ({ ...f, weight_per_unit: weight }));
    if (weight) {
      setSkuComponents(prev => prev.map(c =>
        c.type === 'resin' ? { ...c, qty_per_unit: (Number(weight) * WASTE_FACTOR).toFixed(4) } : c
      ));
    }
  }

  async function save() {
    if (!form.sku || !form.name || form.on_hand === '' || form.reorder_at === '') {
      alert('SKU, name, on hand, and reorder are required.'); return;
    }
    const isCased = form.pack_type === 'cased';
    const payload = {
      sku: form.sku.trim().toUpperCase().replace(/\s+/g, '-'),
      name: form.name.trim(),
      customer: form.customer.trim(),
      supplier_id: form.supplier_id || null,
      upc: form.upc.trim(),
      on_hand: Number(form.on_hand),
      reorder_at: Number(form.reorder_at),
      unit_price: Number(form.unit_price) || 0,
      case_qty: isCased ? (Number(form.case_qty) || 1) : 1,
      bulk_part_id: form.bulk_part_id || null,
      weight_per_unit: Number(form.weight_per_unit) || null,
    };
    let fgId;
    if (editing) {
      await supabase.from('finished_goods').update(payload).eq('id', editing.id);
      fgId = editing.id;
      await supabase.from('sku_components').delete().eq('finished_good_id', fgId);
    } else {
      const { data } = await supabase.from('finished_goods').insert(payload).select();
      fgId = data[0].id;
    }
    const validComponents = skuComponents.filter(c => c.id && c.qty_per_unit);
    if (validComponents.length > 0) {
      await supabase.from('sku_components').insert(validComponents.map(c => ({
        finished_good_id: fgId,
        component_type: c.type,
        resin_id: c.type === 'resin' ? c.id : null,
        component_id: c.type === 'component' ? c.id : null,
        qty_per_unit: Number(c.qty_per_unit),
        per_case: c.per_case || false,
      })));
    }
    setModal(null); load();
  }

  async function applyAdjust(delta, reason) {
    const newVal = Math.max(0, Number(adjusting.on_hand) + delta);
    await supabase.from('finished_goods').update({ on_hand: newVal }).eq('id', adjusting.id);
    await supabase.from('inventory_adjustments').insert({ item_type: 'finished', item_id: adjusting.id, item_name: adjusting.sku + ' - ' + adjusting.name, adjustment: delta, reason });
    setModal(null); load();
  }

  async function del(id) {
    if (!window.confirm('Delete this SKU?')) return;
    await supabase.from('finished_goods').delete().eq('id', id);
    load();
  }

  function isCased(item) { return item.bulk_part_id || Number(item.case_qty) > 1; }
  function adjustUnit(item) { return isCased(item) ? 'cases' : 'units'; }

  function onHandDisplay(item) {
    if (isCased(item)) {
      const cases = Number(item.on_hand);
      const units = cases * (Number(item.case_qty) || 1);
      return (<>
        <span>{cases.toLocaleString()} cases</span>
        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{units.toLocaleString()} units total</div>
      </>);
    }
    return <span>{Number(item.on_hand).toLocaleString()} units</span>;
  }

  // Group items by customer for display
  const grouped = items.reduce((acc, item) => {
    const key = item.customer || 'No Customer';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const autoLbsPerUnit = form.weight_per_unit
    ? (Number(form.weight_per_unit) * WASTE_FACTOR).toFixed(4)
    : null;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Finished Goods</div>
          <div className="page-sub">Grouped by customer · Bulk in units · Cased in cases</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add SKU</button>
      </div>

      {Object.entries(grouped).map(([customer, skus]) => (
        <div key={customer} className="tbl-section">
          <div className="tbl-header">
            <span className="tbl-title" style={{ fontSize: 13, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{customer}</span>
          </div>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr><th>SKU</th><th>Product Name</th><th>Packing</th><th>Weight/Unit</th><th>On Hand</th><th>Reorder At</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {skus.map(item => {
                  const pct = stockPct(item.on_hand, item.reorder_at);
                  const barColor = stockBarColor(item.on_hand, item.reorder_at);
                  return (
                    <tr key={item.id}>
                      <td><span className="badge badge-blue mono">{item.sku}</span></td>
                      <td>{item.name}</td>
                      <td>
                        {item.bulk_part_id
                          ? <span className="badge badge-warn">Pack-out · {item.case_qty}/case</span>
                          : isCased(item)
                            ? <span className="badge badge-blue">Cased · {item.case_qty}/case</span>
                            : <span className="badge badge-gray">Bulk</span>
                        }
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {item.weight_per_unit ? item.weight_per_unit + ' lbs' : '—'}
                      </td>
                      <td className="mono">
                        {onHandDisplay(item)}
                        <div className="progress-wrap"><div className="progress-bar" style={{ width: pct + '%', background: barColor }} /></div>
                      </td>
                      <td className="mono">{Number(item.reorder_at).toLocaleString()} {isCased(item) ? 'cases' : 'units'}</td>
                      <td>{stockBadge(item.on_hand, item.reorder_at)}</td>
                      <td>
                        <div className="flex-row">
                          <button className="btn btn-sm" onClick={() => { setAdjusting(item); setModal('adjust'); }}>+/−</button>
                          <button className="btn btn-sm" onClick={() => openEdit(item)}>Edit</button>
                          <button className="btn btn-sm btn-danger" onClick={() => del(item.id)}>Del</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {items.length === 0 && !loading && (
        <div className="tbl-wrap"><table><tbody><tr className="empty-row"><td colSpan={8}>No SKUs yet.</td></tr></tbody></table></div>
      )}

      {modal === 'form' && (
        <Modal title={editing ? 'Edit SKU — ' + editing.sku : 'Add SKU'} onClose={() => setModal(null)} onSave={save}>
          {/* Basic info */}
          <div className="form-row">
            <div className="form-group"><label className="form-label">SKU *</label><input className="form-input mono" value={form.sku} onChange={set('sku')} placeholder="e.g. DW9-SW" autoFocus /></div>
            <div className="form-group"><label className="form-label">Product Name *</label><input className="form-input" value={form.name} onChange={set('name')} placeholder="e.g. DW9 Tray" /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Customer</label><input className="form-input" value={form.customer} onChange={set('customer')} placeholder="e.g. Sherwin-Williams" /></div>
            <div className="form-group">
              <label className="form-label">Supplier</label>
              <select className="form-input" value={form.supplier_id} onChange={set('supplier_id')}>
                <option value="">— Select supplier —</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">UPC</label><input className="form-input mono" value={form.upc} onChange={set('upc')} placeholder="e.g. 012345678901 (leave blank if none)" /></div>
            <div className="form-group">
              <label className="form-label">Part Weight (lbs/unit)</label>
              <input className="form-input mono" type="number" min="0" step="0.0001" value={form.weight_per_unit} onChange={onWeightChange} placeholder="0.0000" />
              {autoLbsPerUnit && <div className="form-hint">Resin usage auto-set to {autoLbsPerUnit} lbs/unit (weight + 5% waste)</div>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">On Hand *</label><input className="form-input mono" type="number" min="0" value={form.on_hand} onChange={set('on_hand')} placeholder="0" /></div>
            <div className="form-group"><label className="form-label">Reorder At *</label><input className="form-input mono" type="number" min="0" value={form.reorder_at} onChange={set('reorder_at')} placeholder="0" /></div>
          </div>

          {/* Packing type */}
          <div className="form-group">
            <label className="form-label">Packing Type</label>
            <div className="flex-row" style={{ marginBottom: 4 }}>
              <button className={'btn ' + (form.pack_type === 'bulk' ? 'btn-primary' : '')} onClick={() => setForm(f => ({ ...f, pack_type: 'bulk', case_qty: '1', bulk_part_id: '' }))}>Bulk (units)</button>
              <button className={'btn ' + (form.pack_type === 'cased' ? 'btn-primary' : '')} onClick={() => setForm(f => ({ ...f, pack_type: 'cased' }))}>Cased</button>
            </div>
            <div className="form-hint">{form.pack_type === 'bulk' ? 'Tracked in individual units' : 'Tracked in cases'}</div>
          </div>

          {form.pack_type === 'cased' && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Units per Case</label>
                <input className="form-input mono" type="number" min="1" value={form.case_qty} onChange={set('case_qty')} placeholder="1" />
              </div>
              <div className="form-group">
                <label className="form-label">Bulk Part Source</label>
                <select className="form-input" value={form.bulk_part_id} onChange={set('bulk_part_id')}>
                  <option value="">— Direct production —</option>
                  {bulkParts.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <div className="form-hint">Set for PBC/RC only</div>
              </div>
            </div>
          )}

          {/* Components */}
          {!form.bulk_part_id && (
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>Components</span>
                <div className="flex-row">
                  <button className="btn btn-sm" onClick={() => addSkuComponent('resin')}>+ Resin</button>
                  <button className="btn btn-sm" onClick={() => addSkuComponent('component')}>+ Label/Box/Foam</button>
                </div>
              </div>

              {skuComponents.length === 0 && <div style={{ fontSize: 12, color: 'var(--muted)', padding: '4px 0' }}>No components linked yet.</div>}

              {skuComponents.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 110px 28px', gap: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>Component</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>Qty</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>Per</div>
                  <div></div>
                </div>
              )}

              {skuComponents.map((c, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 110px 28px', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <select className="form-input" value={c.id} onChange={e => updateSkuComponent(idx, 'id', e.target.value)}>
                    <option value="">— Select —</option>
                    {c.type === 'resin'
                      ? resins.map(r => <option key={r.id} value={r.id}>{r.name}</option>)
                      : components.map(comp => <option key={comp.id} value={comp.id}>{comp.name} ({comp.category})</option>)
                    }
                  </select>
                  <input
                    className="form-input mono"
                    type="number" min="0" step="0.0001"
                    value={c.qty_per_unit}
                    onChange={e => updateSkuComponent(idx, 'qty_per_unit', e.target.value)}
                    placeholder={c.type === 'resin' ? 'lbs' : 'qty'}
                  />
                  {c.type !== 'resin' && form.pack_type === 'cased' ? (
                    <div className="flex-row" style={{ gap: 4 }}>
                      <button className={'btn btn-sm ' + (!c.per_case ? 'btn-primary' : '')} style={{ flex: 1, justifyContent: 'center', fontSize: 11 }} onClick={() => updateSkuComponent(idx, 'per_case', false)}>per unit</button>
                      <button className={'btn btn-sm ' + (c.per_case ? 'btn-primary' : '')} style={{ flex: 1, justifyContent: 'center', fontSize: 11 }} onClick={() => updateSkuComponent(idx, 'per_case', true)}>per case</button>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.type === 'resin' ? 'lbs/unit' : 'per unit'}</div>
                  )}
                  <button className="btn btn-sm btn-danger" onClick={() => removeSkuComponent(idx)} style={{ padding: '4px 7px' }}>✕</button>
                </div>
              ))}

              {skuComponents.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                  Resin lbs auto-calculated from part weight + 5% runner/waste
                </div>
              )}
            </div>
          )}

          {form.bulk_part_id && (
            <div style={{ background: 'var(--accent-light)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginTop: 12, fontSize: 12, color: 'var(--accent)' }}>
              Pack-out SKU — labels and foam consumed during production run. Only the customer box is consumed at pack-out.
            </div>
          )}
        </Modal>
      )}

      {modal === 'adjust' && adjusting && (
        <AdjustModal item={adjusting} unit={adjustUnit(adjusting)} onClose={() => setModal(null)} onSave={applyAdjust} />
      )}
    </div>
  );
}
